"use client";

import React, { useEffect, useState } from "react";
import styles from "./stem-splitter-page.module.scss";
import StemSplitterCategoriesBar from "@/components/pages/stem-splitter/components/stem-splitter-categories-bar";
import { useSplitterProgressContext } from "@/components/pages/stem-splitter/components/providers/stem-splitter-progress-provider";
import StemSplitButton from "@/components/pages/stem-splitter/components/stem-split-button";
import ProgressBar from "@/components/pages/convert/components/progress-bar";
import { useCancelSplit } from "@/components/pages/stem-splitter/hooks/use-cancel-split";
import SplitterFaq from "@/components/pages/stem-splitter/components/splitter-faq";
import { useProfileContext } from "@/providers/profile-provider/profile-provider";
import ResetButton from "@/components/pages/stem-splitter/components/reset-button";
import StemResultItem from "@/components/pages/stem-splitter/components/stem-result-item";
import UpgradeToDownload from "@/components/common/upgrade-to-download";
import ServicePageHeader from "@/components/common/service-page-header";
import StemDownloadZipButton from "@/components/pages/stem-splitter/components/stem-download-zip-button";
import { stemSplitterLicenseManager } from "@/services/license/license-managers/stem-splitter-license-manager";
import MediaFormatsInput from "@/components/common/input/media-formats-input";
import { getFileNameFromUrl } from "@/utils/history-helper";
import {
  shouldDisplayProgress,
  shouldDisplayStartProcessButton,
} from "@/hooks/progress-context/use-anonymous-progress-provider";

const StemSplitterPage = () => {
  const progressContext = useSplitterProgressContext();
  const { cancelSplit, isCancelling } = useCancelSplit();
  const { license, isLoading } = useProfileContext();
  const [stemResults, setStemResults] = useState<{
    [key: string]: string | undefined;
  }>({});

  const getFile = async (fileName: string) => {
    const resp = await fetch(
      "/api/split/get?" +
        new URLSearchParams({
          fileName: fileName,
        }),
    );
    if (resp.status === 200) {
      return await resp.text();
    }
    return undefined;
  };

  useEffect(() => {
    const fetchResults = async () => {
      const results = {};
      for (const [stem, url] of Object.entries(progressContext.results)) {
        if (url !== undefined) {
          results[stem] = await getFile(url);
        }
      }
      setStemResults(results);
    };

    if (progressContext.status === "complete") {
      fetchResults();
    }
  }, [progressContext.status, progressContext.results]);
  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <ServicePageHeader
          title={
            <>
              Stem <span>Splitter</span>
            </>
          }
          description={
            "Start with a full track. Split it into six stems: guitar, piano, drums, bass, vocals and others."
          }
        />
      </div>
      <div className={styles.container}>
        <div className={styles.input_wrapper}>
          <MediaFormatsInput
            localFileInputProps={{
              isLimited: false,
              license: license,
            }}
          />
          <div className={styles.categories}>
            <StemSplitterCategoriesBar />
          </div>
        </div>

        {shouldDisplayStartProcessButton(progressContext.status) && (
          <div className={styles.buttons}>
            <ResetButton />
            <StemSplitButton />
          </div>
        )}

        {shouldDisplayProgress(progressContext.status) && (
          <ProgressBar
            percent={progressContext.percent}
            descriptionCode={progressContext.description}
            onCancel={cancelSplit}
            isCancelling={isCancelling}
          />
        )}

        {progressContext.status === "complete" && (
          <>
            <div className={styles.results}>
              <p className={styles.song_title}>{getFileName(stemResults)}</p>
              {Object.entries(stemResults).map(([stem, url]) => {
                return (
                  <StemResultItem
                    url={url!}
                    stem={stem}
                    key={stem}
                    id={progressContext.processId}
                    featureType={"stem-splitter"}
                  />
                );
              })}
              <UpgradeToDownload license={license} isLoading={isLoading} />
            </div>
            {stemSplitterLicenseManager.downloadResultLimiter.isDownloadEnabled(
              license,
            ) && (
              <StemDownloadZipButton
                urls={Object.entries(stemResults)
                  .map(([_, url]) => url!)
                  .filter((url) => url !== undefined)}
              />
            )}
          </>
        )}

        <SplitterFaq />
      </div>
    </div>
  );
};

const getFileName = (stemResults: { [p: string]: string | undefined }) => {
  const result = getFileNameFromUrl(
    stemResults[Object.keys(stemResults)[0]] ?? "",
    Object.keys(stemResults)[0],
  );
  return result.split("_").slice(1).join(" ");
};

export default StemSplitterPage;
